<template>
  <div id="events-bidding-list" class="page-container">
    <v-overlay :value="OverlayLoadingFlag">
      <v-progress-circular indeterminate size="64"></v-progress-circular>
    </v-overlay>

    <!-- Premium Page Header -->
    <v-card elevation="20" class="header-card mb-6 overflow-hidden">
      <div class="header-gradient">
        <div class="header-content pa-8">
          <v-row align="center" justify="space-between">
            <v-col cols="12" md="6">
              <div class="d-flex align-center">
                <div class="header-icon-box">
                  <v-icon size="48" color="white">mdi-gavel</v-icon>
                </div>
                <div class="ml-6">
                  <h1 class="header-title white--text mb-1">{{ PageTitle }}</h1>
                  <p class="header-subtitle white--text mb-0">
                    View and manage event bidding proposals
                  </p>
                </div>
              </div>
            </v-col>
            <v-col cols="12" md="6" class="text-right">
              <div class="d-flex justify-end flex-wrap gap-2">
                <!-- Refresh Button -->
                <v-btn
                  @click.prevent="refreshPageData"
                  color="rgba(255,255,255,0.2)"
                  elevation="0"
                  rounded
                  large
                  class="action-btn"
                >
                  <v-icon left>mdi-refresh</v-icon>
                  Refresh
                </v-btn>
              </div>
            </v-col>
          </v-row>
        </div>
      </div>
    </v-card>

    <!-- Event Bidding Approval Dialog -->
    <v-dialog v-model="previewRecordPrompt" persistent max-width="75%">
      <events-bidding-approval
        pageTitle="Event Bid Approval"
        pageDescription="Event Bid Approval"
        :previewRecordPrompt="previewRecordPrompt"
        @hidePreviewRecordPrompt="hidePreviewRecordPrompt($event)"
        :recordData="selectedData"
        v-if="previewRecordPrompt"
      ></events-bidding-approval>
    </v-dialog>

    <!-- Filter Card -->
    <v-card elevation="8" class="filter-card mb-4">
      <v-card-text class="pa-5">
        <!-- Loading -->
        <div v-if="LoadingFlag" class="text-center py-8">
          <v-progress-circular
            :size="70"
            :width="7"
            color="primary"
            indeterminate
          ></v-progress-circular>
          <p class="mt-4 text-h6 primary--text">Loading bidding data...</p>
        </div>

        <v-form
          ref="form1"
          v-model="valid1"
          lazy-validation
          v-if="!LoadingFlag"
        >
          <!-- Filter Header -->
          <div class="filter-header mb-5">
            <v-icon color="primary" size="32" class="mr-2"
              >mdi-filter-variant</v-icon
            >
            <span class="text-h5 font-weight-bold primary--text"
              >Search & Filter</span
            >
          </div>

          <div class="form-section mb-4">
            <v-row wrap>
              <v-col cols="12" sm="6" lg="3" md="3">
                <div class="field-label">
                  <v-icon size="18" class="mr-1">mdi-map-marker</v-icon>
                  Zone
                </div>
                <v-autocomplete
                  @change="getLomCodeOptions"
                  :reduce="(option) => option.value"
                  :loading="ZoneCodeOptionsLoading"
                  :items="ZoneCodeOptions"
                  :rules="ZoneCodeRules"
                  v-model="ZoneCode"
                  dense
                  outlined
                  clearable
                  prepend-inner-icon="mdi-map-marker-radius"
                  placeholder="Select zone..."
                ></v-autocomplete>
              </v-col>

              <v-col cols="12" sm="6" lg="3" md="3">
                <div class="field-label">
                  <v-icon size="18" class="mr-1">mdi-calendar</v-icon>
                  From Date
                </div>
                <v-menu
                  v-model="Modal1"
                  :close-on-content-click="false"
                  :nudge-right="40"
                  transition="scale-transition"
                  offset-y
                  min-width="auto"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      v-model="FromDate"
                      readonly
                      outlined
                      dense
                      v-bind="attrs"
                      v-on="on"
                      prepend-inner-icon="mdi-calendar-check"
                      placeholder="Select from date..."
                    ></v-text-field>
                  </template>
                  <v-date-picker
                    v-model="FromDate"
                    @input="Modal1 = false"
                  ></v-date-picker>
                </v-menu>
              </v-col>

              <v-col cols="12" sm="6" lg="3" md="3">
                <div class="field-label">
                  <v-icon size="18" class="mr-1">mdi-calendar</v-icon>
                  To Date
                </div>
                <v-menu
                  v-model="Modal2"
                  :close-on-content-click="false"
                  :nudge-right="40"
                  transition="scale-transition"
                  offset-y
                  min-width="auto"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      v-model="ToDate"
                      readonly
                      v-bind="attrs"
                      v-on="on"
                      outlined
                      dense
                      prepend-inner-icon="mdi-calendar-range"
                      placeholder="Select to date..."
                    ></v-text-field>
                  </template>
                  <v-date-picker
                    v-model="ToDate"
                    @input="Modal2 = false"
                  ></v-date-picker>
                </v-menu>
              </v-col>
            </v-row>
          </div>

          <v-row wrap class="mt-4">
            <v-col align="center" cols="12" md="12">
              <v-btn
                :disabled="!valid1"
                @click.prevent="searchForm"
                :loading="SearchFlag"
                color="#667eea"
                elevation="8"
                rounded
                large
                class="search-btn font-weight-bold px-8"
              >
                <v-icon left>mdi-magnify</v-icon>
                Search
              </v-btn>
            </v-col>
          </v-row>
        </v-form>
      </v-card-text>
    </v-card>

    <!-- No Records Alert -->
    <v-card
      elevation="8"
      class="mb-4"
      v-if="!LoadingFlag && tableData1.length === 0"
    >
      <v-card-text class="pa-6">
        <v-alert type="info" dense outlined icon="mdi-information" prominent>
          <v-alert-title>No Records Found</v-alert-title>
          Please adjust your search filters and try again.
        </v-alert>
      </v-card-text>
    </v-card>

    <!-- Results Section -->
    <div v-if="tableData1.length > 0">
      <!-- Stats Bar -->
      <v-card elevation="8" class="stats-card mb-4">
        <v-card-text class="pa-4">
          <v-row align="center">
            <v-col cols="12" md="6">
              <div class="d-flex align-center">
                <div class="stats-icon-box">
                  <v-icon size="32" color="white">mdi-file-document</v-icon>
                </div>
                <div class="ml-4" color="white">
                  <p class="text-subtitle2 font-weight-bold mb-0">
                    Total Records
                  </p>
                  <p class="text-h4 font-weight-bold mb-0">
                    {{ tableData1.length }}
                  </p>
                </div>
              </div>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>

      <!-- Search Bar -->
      <v-card elevation="8" class="search-card mb-4">
        <v-card-text class="pa-4">
          <v-text-field
            v-model="search"
            placeholder="🔍 Search records here..."
            outlined
            dense
            clearable
            prepend-inner-icon="mdi-magnify"
            class="search-input"
          >
            <template v-slot:append>
              <v-icon color="primary">mdi-magnify</v-icon>
            </template>
          </v-text-field>
        </v-card-text>
      </v-card>

      <!-- Beautiful Data Table -->
      <v-card elevation="12" class="table-card">
        <v-data-table
          class="elevation-1 stunning-table"
          loading-text="Loading... Please wait"
          v-model="selected"
          :search="search"
          :loading="SearchFlag"
          :item-key="tableOptions1.ItemKey"
          :single-select="tableOptions1.SingleSelectFlag"
          :headers="tableColumns1"
          :items="tableData1"
          :items-per-page="tableOptions1.ItemsPerPage"
          :footer-props="tableOptions1.FooterProps"
        >
          <template v-slot:[`item.StatusTxt`]="{ item }">
            <v-chip
              :color="item.StatusColor"
              dark
              class="font-weight-bold status-chip"
            >
              {{ item.StatusTxt }}
            </v-chip>
          </template>

          <template v-slot:[`item.actions`]="{ item }">
            <div class="action-buttons">
              <v-tooltip bottom v-if="item.EditFlag">
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    icon
                    small
                    color="#667eea"
                    @click="previewData(item)"
                    v-bind="attrs"
                    v-on="on"
                    class="action-icon-btn"
                  >
                    <v-icon size="20">mdi-check-circle-outline</v-icon>
                  </v-btn>
                </template>
                <span>Event Bid Approval</span>
              </v-tooltip>

              <v-tooltip bottom v-if="item.EditFlag">
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    :href="item.FilePath"
                    target="_blank"
                    icon
                    small
                    color="#10b981"
                    v-bind="attrs"
                    v-on="on"
                    class="action-icon-btn"
                  >
                    <v-icon size="20">mdi-file-pdf-box</v-icon>
                  </v-btn>
                </template>
                <span>Download PDF</span>
              </v-tooltip>

              <v-tooltip bottom v-if="item.DeleteFlag">
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    icon
                    small
                    color="#f44336"
                    @click="deleteConfirmAlert(item)"
                    v-bind="attrs"
                    v-on="on"
                    class="action-icon-btn"
                  >
                    <v-icon size="20">mdi-trash-can-outline</v-icon>
                  </v-btn>
                </template>
                <span>Delete Record</span>
              </v-tooltip>
            </div>
          </template>
        </v-data-table>
      </v-card>
    </div>
  </div>
</template>

<script>
import common from "@/view/Common.vue";
import companyConfig from "@/company_config.json";
import Swal from "sweetalert2";
import EventsBiddingApproval from "@/view/pages/erp/events-bidding/EventsBiddingApproval.vue";

export default {
  mixins: [common],
  components: { EventsBiddingApproval },
  data() {
    return {
      valid1: true,
      OverlayLoadingFlag: false,
      LoadingFlag: false,
      SearchFlag: false,
      uploadPercentage: 0,

      EditRecordPrompt: false,

      EventTypeRules: [],
      EventType: "",
      EventTypeOptions: [
        { value: "", text: "- Select -" },
        { value: 1, text: "National Events" },
        { value: 2, text: "Zone Events" },
        // { value: 3, text: "Lom Events" }
      ],
      EventTypeOptionsLoading: false,

      ZoneCodeRules: [],
      ZoneCode: "",
      ZoneCodeOptions: [],
      ZoneCodeOptionsLoading: false,

      LomCodeRules: [],
      LomCode: "",
      LomCodeOptions: [],
      LomCodeOptionsLoading: false,
      previewRecordPrompt: false,

      FromDate: new Date().toISOString().slice(0, 10),
      Modal1: false,

      ToDate: new Date().toISOString().slice(0, 10),
      Modal2: false,

      tableData1: [],
      tableColumns1: [],
      tableOptions1: [],
      selected: [],
      search: "",
      selectedData: {},

      PageInfo: {},
      PageTitle: "",
      PageDescription: "",
      CurrentYearId: 0,
      JciYearCode: "",
      YearName: "",
    };
  },
  mounted() {},
  watch: {
    PageInfo: function () {
      console.log("watch PageInfo");
      this.LoadingFlag = false;
      var tr = this.PageInfo;
      console.log({ tr });
      var n1 = Object.keys(tr).length;
      console.log({ n1 });
      if (n1 > 0) {
        var flag = tr.PagePermissionFlag;
        console.log("PagePermissionFlag=" + flag);

        if (!flag) {
          this.$router.push("/access-denied");
        }
        this.PageTitle = tr.PageTitle;
        this.PageDescription = tr.PageDescription;

        var CurrentJciYearId = parseInt(tr.CurrentJciYearId);
        console.log({ CurrentJciYearId });
        this.JciYearCode = CurrentJciYearId;

        var YearName = parseInt(tr.CurrentJciYearName);
        console.log({ YearName });
        this.YearName = YearName;

        this.CurrentYearId = CurrentJciYearId;

        this.getZoneCodeOptions();
        this.getTableRecords();
      }
    },
    ZoneCodeOptions: function () {
      console.log("watch ZoneCodeOptions");
      this.ZoneCodeOptionsLoading = false;
    },
    LomCodeOptions: function () {
      console.log("watch LomCodeOptions");
      this.LomCodeOptionsLoading = false;
    },
    EventType: function () {
      console.log("watch EventType");

      this.ZoneCode = "";
      this.LomCodeOptions = [];
      this.LomCode = "";
    },
  },
  methods: {
    refreshPageData() {
      console.log("refreshPageData called");
      this.LoadingFlag = true;
      var url1 = "api/menu-details/show";
      var condition1 = {
        UserInterface: 1,
        Module: "events_bidding",
        Action: "report_1",
      };
      this.getPageDetails("PageInfo", {}, url1, condition1);
    },
    hidePreviewRecordPrompt(flag) {
      console.log("hidePreviewRecordPrompt called");
      console.log({ flag });
      this.previewRecordPrompt = false;
      if (flag) {
        this.getTableRecords();
      }
    },
    previewData(tr) {
      console.log("previewData called");
      // var tr = this.selected
      // var n1 = tr.length
      var n1 = Object.keys(tr).length;
      console.log("n1=" + n1 + ", tr=" + JSON.stringify(tr));
      if (n1 > 0) {
        this.previewRecordPrompt = true;
        this.selectedData = tr;
      } else {
        var message = "Kindly select one record to preview";
        this.toast("error", message);
      }
    },
    getZoneCodeOptions() {
      console.log("getZoneCodeOptions called");
      var yearCode = this.JciYearCode;
      console.log("yearCode=" + yearCode);
      if (yearCode != "") {
        this.ZoneCodeOptionsLoading = true;
        var selectbox1_source = "ZoneCode";
        var selectbox1_destination = "ZoneCodeOptions";
        var selectbox1_url = "api/year-wise-lom/zone-options";
        var selectbox1_conditions_array = {
          UserInterface: 2,
          YearCode: yearCode,
        };
        this.getSelectBoxOptions(
          selectbox1_source,
          selectbox1_destination,
          selectbox1_conditions_array,
          selectbox1_url
        );
      } else {
        var message = "";
        if (yearCode == "") {
          message += "Year should not be empty. ";
        }
        this.toast("error", message);
      }
    },
    getLomCodeOptions() {
      console.log("getLomCodeOptions called");
      var YearCode = this.JciYearCode;
      var ZoneCode = this.ZoneCode;
      this.LomCode = "";
      console.log("YearCode=" + YearCode + ", ZoneCode=" + ZoneCode);
      if (YearCode != "" && ZoneCode != "") {
        this.LomCodeOptionsLoading = true;
        var selectbox1_source = "LomCode";
        var selectbox1_destination = "LomCodeOptions";
        var selectbox1_url = "api/year-wise-lom/lom-options";
        var selectbox1_conditions_array = {
          UserInterface: 3,
          YearCode: YearCode,
          ZoneCode: ZoneCode,
          AllOption: 0,
        };
        this.getSelectBoxOptions(
          selectbox1_source,
          selectbox1_destination,
          selectbox1_conditions_array,
          selectbox1_url
        );
      } else {
        var message = "";
        if (YearCode == "") {
          message += "Year should not be empty. ";
        }
        if (ZoneCode == "") {
          message += "Zone should not be empty. ";
        }
        this.toast("error", message);
      }
    },
    searchForm() {
      console.log("searchForm is called");

      var validate1 = this.$refs.form1.validate();
      console.log("validate1=" + validate1);

      if (validate1) {
        this.getTableRecords();
      } else {
        var message = "";
        if (!validate1) {
          message += "Kindly fill the required fields";
        }
        this.toast("error", message);
      }
    },
    getTableRecords() {
      this.LoadingFlag = true;
      this.SearchFlag = true;
      this.tableData1 = [];

      var server_url = companyConfig.apiURL;
      var token = this.$session.get("token");
      token = token == (null || undefined) ? 0 : token;
      var add_url = server_url + "api/events/bidding/lists";
      var upload = {
        UserInterface: 1,
        ZoneCode: this.ZoneCode,
        FromDate: this.FromDate,
        ToDate: this.ToDate,
      };
      console.log("upload=" + JSON.stringify(upload));
      console.log(
        "server_url=" + server_url + ", token=" + token + ", add_url=" + add_url
      );

      const thisIns = this;
      var output = "";
      var records = {};
      var flag = 0;

      this.$http({
        url: add_url,
        method: "POST",
        data: upload,
        headers: {
          "Content-Type": "application/json",
          Authorization: "Bearer " + token,
        },
        onUploadProgress: function (progressEvent) {
          this.uploadPercentage = parseInt(
            Math.round((progressEvent.loaded / progressEvent.total) * 100)
          );
        }.bind(this),
      })
        .then(function (response) {
          console.log("response=" + JSON.stringify(response));

          // thisIns.pageLoadingOff()
          thisIns.LoadingFlag = false;
          thisIns.SearchFlag = false;

          output = response.data.output;
          flag = response.data.flag;
          output = response.data.output;
          console.log("output=" + output + ", flag=" + flag);

          records = response.data.records;
          console.log({ records });

          if (flag == 1) {
            var options = records.TableOptions;
            console.log({ options });
            thisIns.tableOptions1 = options;
            thisIns.tableColumns1 = records.TableHeader;
            thisIns.tableData1 = records.TableData;
            thisIns.toast("success", output);
          } else {
            thisIns.toast("error", output);
          }
        })
        .catch(function (error) {
          thisIns.LoadingFlag = false;
          thisIns.SearchFlag = false;

          console.log("error=" + error);
          thisIns.toast("error", error);
        });
    },
    editData(tr) {
      console.log("editData called");
      var n1 = Object.keys(tr).length;
      console.log("n1=" + n1 + ", tr=" + JSON.stringify(tr));

      if (n1 > 0) {
        this.EditRecordPrompt = true;
        this.selectedData = tr;
      } else {
        var message = "Kindly select one city to edit";
        this.toast("error", message);
      }
    },
    hideEditRecordPrompt() {
      console.log("hideEditRecordPrompt called");
      this.EditRecordPrompt = false;
      this.searchForm();
    },
    deleteConfirmAlert(tr) {
      console.log("deleteConfirmAlert called");
      var message = "";
      var n1 = Object.keys(tr).length;
      console.log("n1=" + n1 + ", tr=" + JSON.stringify(tr));

      if (n1 > 0) {
        var htmlTxt = "";
        htmlTxt += "<br/> Event Name <b> " + tr.Title + "</b>";
        htmlTxt += "<br/> Event Type <b> " + tr.OrganizationTypeName + "</b>";

        Swal.fire({
          title: "Do you want to continue?",
          text: "Please verify all the information before proceeding.",
          icon: "warning",
          html: htmlTxt,
          showCancelButton: true,
          confirmButtonText: `Delete`,
          confirmButtonColor: "#d33",
          cancelButtonText: `Cancel`,
          cancelButtonColor: "#3085d6",
        }).then((result) => {
          /* Read more about isConfirmed, isDenied below */
          if (result.isConfirmed) {
            this.deleteData(tr);
          } else {
            this.sweetAlert("error", "You cancelled the process", true);
          }
        });
      } else {
        if (n1 == 0) {
          message += "Kindly select one record to delete. ";
        }
        this.sweetAlert("error", message, false);
      }
    },
    deleteData(tr) {
      console.log("deleteData is called");

      var EventId = tr.EventId;
      var EventType = tr.OrganizationTypeId;
      console.log("EventId=" + EventId + "; EventType=" + EventType);

      this.DeleteLoadingFlag = true;

      var server_url = companyConfig.apiURL;
      var token = this.$session.get("token");
      token = token == (null || undefined) ? 0 : token;
      var add_url = server_url + "api/tenetative-event/destroy";
      var upload = {
        UserInterface: 1,
        Event: EventId,
        EventType: EventType,
      };
      console.log("upload=" + JSON.stringify(upload));
      console.log(
        "server_url=" + server_url + ", token=" + token + ", add_url=" + add_url
      );

      const thisIns = this;
      var output = "";
      var flag = 0;

      this.$http({
        url: add_url,
        method: "POST",
        data: upload,
        headers: {
          "Content-Type": "application/json",
          Authorization: "Bearer " + token,
        },
        onUploadProgress: function (progressEvent) {
          this.uploadPercentage = parseInt(
            Math.round((progressEvent.loaded / progressEvent.total) * 100)
          );
        }.bind(this),
      })
        .then(function (response) {
          console.log("response=" + JSON.stringify(response));

          // thisIns.pageLoadingOff()
          thisIns.DeleteLoadingFlag = false;

          output = response.data.output;
          flag = response.data.flag;
          output = response.data.output;
          console.log("output=" + output + ", flag=" + flag);

          if (flag == 1) {
            thisIns.sweetAlert("success", output, true);
            thisIns.searchForm();
          } else {
            thisIns.sweetAlert("error", output, false);
          }
        })
        .catch(function (error) {
          console.log("error=" + error);
          thisIns.LoadingFlag = false;
        });
    },
  },
  beforeMount() {
    this.refreshPageData();
  },
};
</script>
<style lang="scss" scoped>
// Color Palette
$primary-color: #667eea;
$secondary-color: #764ba2;
$gradient-main: linear-gradient(
  135deg,
  $primary-color 0%,
  $secondary-color 100%
);
$success-color: #10b981;
$danger-color: #f44336;
$warning-color: #ff7f50;
$card-shadow: 0 16px 48px rgba(102, 126, 234, 0.15);
$input-shadow: 0 4px 12px rgba(102, 126, 234, 0.1);

// Animation Keyframes
@keyframes slideDown {
  from {
    opacity: 0;
    transform: translateY(-20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes slideInLeft {
  from {
    opacity: 0;
    transform: translateX(-30px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

@keyframes cardSlideIn {
  from {
    opacity: 0;
    transform: translateY(40px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

@keyframes float {
  0%,
  100% {
    transform: translateY(0px);
  }
  50% {
    transform: translateY(-10px);
  }
}

@keyframes rotate {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

#events-bidding-list {
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
  min-height: 100vh;
  padding: 30px 15px;
  animation: fadeIn 0.6s ease-in-out;

  // ============================================
  // HEADER CARD
  // ============================================
  .header-card {
    border-radius: 20px !important;
    overflow: hidden;
    animation: slideDown 0.6s ease-out;
    border: none;
    box-shadow: 0 20px 60px rgba(102, 126, 234, 0.4);
  }

  .header-gradient {
    background: $gradient-main;
    position: relative;
    overflow: hidden;

    &::before {
      content: "";
      position: absolute;
      top: -50%;
      right: -50%;
      width: 200%;
      height: 200%;
      background: radial-gradient(
        circle,
        rgba(255, 255, 255, 0.1) 0%,
        transparent 70%
      );
      animation: rotate 20s linear infinite;
    }
  }

  .header-content {
    position: relative;
    z-index: 1;
  }

  .header-icon-box {
    background: rgba(255, 255, 255, 0.2);
    backdrop-filter: blur(10px);
    border-radius: 20px;
    padding: 15px;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
    animation: float 3s ease-in-out infinite;
  }

  .header-title {
    font-size: 32px;
    font-weight: 700;
    text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.2);
    animation: fadeInUp 0.8s ease-out;
    letter-spacing: 0.5px;
  }

  .header-subtitle {
    font-size: 16px;
    opacity: 0.95;
    animation: fadeInUp 1s ease-out;
    letter-spacing: 0.3px;
  }

  .action-btn {
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    font-weight: 700;
    letter-spacing: 0.5px;
    text-transform: uppercase;
    background: rgba(255, 255, 255, 0.15) !important;
    border: 2px solid rgba(255, 255, 255, 0.3) !important;
    backdrop-filter: blur(10px);
    color: white !important;

    &:hover {
      transform: translateY(-4px);
      box-shadow: 0 12px 24px rgba(0, 0, 0, 0.3) !important;
      background: rgba(255, 255, 255, 0.25) !important;
    }
  }

  // ============================================
  // FILTER CARD
  // ============================================
  .filter-card {
    border-radius: 16px !important;
    animation: fadeIn 0.8s ease-out;
    border: none;
    box-shadow: 0 12px 40px rgba(102, 126, 234, 0.2);
  }

  .filter-header {
    display: flex;
    align-items: center;
    padding-bottom: 16px;
    border-bottom: 3px solid $primary-color;
    margin-bottom: 24px;
    animation: slideInLeft 0.8s ease-out;
  }

  .form-section {
    background: linear-gradient(
      135deg,
      rgba(102, 126, 234, 0.03) 0%,
      rgba(118, 75, 162, 0.03) 100%
    );
    border: 1px solid rgba(102, 126, 234, 0.1);
    border-radius: 12px;
    padding: 20px;
  }

  .field-label {
    display: flex;
    align-items: center;
    font-weight: 600;
    color: #004e92;
    margin-bottom: 8px;
    font-size: 14px;

    .v-icon {
      color: $primary-color;
    }
  }

  .search-btn {
    font-weight: 700;
    letter-spacing: 1px;
    transition: all 0.3s ease;
    text-transform: uppercase;

    &:hover {
      transform: scale(1.05);
      box-shadow: 0 8px 20px rgba(102, 126, 234, 0.4) !important;
    }

    &:disabled {
      opacity: 0.6;
    }
  }

  // ============================================
  // STATS CARD
  // ============================================
  .stats-card {
    border-radius: 16px !important;
    background: $gradient-main;
    color: white;
    animation: slideInLeft 0.8s ease-out;
    border: none;
    box-shadow: 0 12px 40px rgba(102, 126, 234, 0.2);
  }

  .stats-icon-box {
    background: rgba(255, 255, 255, 0.2);
    backdrop-filter: blur(10px);
    border-radius: 50%;
    width: 64px;
    height: 64px;
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  }

  // ============================================
  // SEARCH CARD
  // ============================================
  .search-card {
    border-radius: 16px !important;
    animation: fadeIn 1s ease-out;
    border: none;
    box-shadow: 0 12px 40px rgba(102, 126, 234, 0.2);
  }

  .search-input {
    ::v-deep .v-input__slot {
      border-radius: 50px !important;
      border: 2px solid $primary-color !important;
      transition: all 0.3s ease;

      &:hover {
        box-shadow: 0 4px 16px rgba(102, 126, 234, 0.3);
      }
    }
  }

  // ============================================
  // TABLE CARD & STYLING
  // ============================================
  .table-card {
    border-radius: 16px !important;
    overflow: hidden;
    animation: fadeInUp 1s ease-out;
    border: none;
    box-shadow: 0 20px 60px rgba(102, 126, 234, 0.2);
  }

  .stunning-table {
    ::v-deep {
      thead {
        background: $gradient-main;

        th {
          color: white !important;
          font-weight: 700 !important;
          font-size: 14px !important;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          padding: 20px 16px !important;
          border: none !important;
          background: $gradient-main !important;
        }
      }

      tbody {
        tr {
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          border-bottom: 1px solid rgba(102, 126, 234, 0.1) !important;

          &:hover {
            background: linear-gradient(
              90deg,
              rgba(102, 126, 234, 0.08) 0%,
              rgba(118, 75, 162, 0.08) 100%
            ) !important;
            transform: scale(1.01);
            box-shadow: inset 0 0 20px rgba(102, 126, 234, 0.1);
          }

          td {
            padding: 16px !important;
            font-size: 14px;
            font-weight: 500;
          }
        }
      }

      .v-data-table__wrapper {
        border-radius: 12px;
      }
    }
  }

  .status-chip {
    font-weight: 700 !important;
    letter-spacing: 0.3px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
    transition: all 0.3s ease;

    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.25);
    }
  }

  .action-buttons {
    display: flex;
    gap: 8px;
    justify-content: center;
    align-items: center;
  }

  .action-icon-btn {
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);

    &:hover {
      transform: scale(1.3) translateY(-3px);
      box-shadow: 0 6px 16px rgba(0, 0, 0, 0.2);
    }
  }

  // ============================================
  // RESPONSIVE DESIGN
  // ============================================
  @media (max-width: 1200px) {
    .header-title {
      font-size: 26px;
    }

    .header-subtitle {
      font-size: 14px;
    }
  }

  @media (max-width: 960px) {
    padding: 20px 10px;

    .header-card {
      margin-bottom: 16px;
    }

    .header-title {
      font-size: 22px;
      margin-bottom: 4px !important;
    }

    .header-subtitle {
      font-size: 12px;
    }

    .header-icon-box {
      width: 50px;
      height: 50px;
      padding: 12px;
    }

    .action-btn {
      font-size: 12px !important;
      padding: 8px 12px !important;

      .v-icon {
        font-size: 16px !important;
      }
    }

    .filter-card {
      margin-bottom: 12px;
    }

    .form-section {
      padding: 12px;
    }

    .search-btn {
      font-size: 12px !important;
      padding: 8px 16px !important;
    }

    .stats-card {
      margin-bottom: 12px;
    }

    .search-card {
      margin-bottom: 12px;
    }

    .action-icon-btn {
      &:hover {
        transform: scale(1.15) translateY(-2px);
      }
    }
  }

  @media (max-width: 600px) {
    padding: 15px 8px;

    .header-card {
      margin-bottom: 12px;

      ::v-deep .v-card__text {
        padding: 12px !important;
      }
    }

    .header-title {
      font-size: 18px;
      margin-bottom: 2px !important;
    }

    .header-subtitle {
      font-size: 11px;
    }

    .header-icon-box {
      width: 40px;
      height: 40px;
      padding: 8px;

      ::v-deep .v-icon {
        font-size: 32px !important;
      }
    }

    .action-btn {
      font-size: 10px !important;
      padding: 6px 10px !important;

      .v-icon {
        font-size: 14px !important;
      }
    }

    .filter-card {
      margin-bottom: 8px;

      ::v-deep .v-card__text {
        padding: 12px !important;
      }
    }

    .filter-header {
      margin-bottom: 12px;

      .v-icon {
        font-size: 24px !important;
      }

      span {
        font-size: 14px !important;
      }
    }

    .form-section {
      padding: 8px;
    }

    .search-btn {
      font-size: 10px !important;
      padding: 6px 12px !important;
    }

    .stats-card {
      margin-bottom: 8px;

      ::v-deep .v-card__text {
        padding: 12px !important;
      }
    }

    .search-card {
      margin-bottom: 8px;

      ::v-deep .v-card__text {
        padding: 8px !important;
      }
    }

    .action-buttons {
      flex-wrap: wrap;
      gap: 4px;
    }

    .action-icon-btn {
      &:hover {
        transform: scale(1.1) translateY(-2px);
      }
    }
  }

  // ============================================
  // UTILITY CLASSES
  // ============================================
  .gap-2 {
    gap: 8px;
  }

  .text-danger {
    color: $danger-color;
  }

  .text-right {
    text-align: right;
  }

  .justify-end {
    justify-content: flex-end;
  }

  .flex-wrap {
    flex-wrap: wrap;
  }

  .ml-6 {
    margin-left: 24px;
  }

  .ml-4 {
    margin-left: 16px;
  }

  .mb-6 {
    margin-bottom: 24px;
  }

  .mb-4 {
    margin-bottom: 16px;
  }

  .mb-1 {
    margin-bottom: 4px;
  }

  .mb-0 {
    margin-bottom: 0;
  }

  .py-8 {
    padding-top: 32px;
    padding-bottom: 32px;
  }

  .py-4 {
    padding-top: 16px;
    padding-bottom: 16px;
  }

  .mt-4 {
    margin-top: 16px;
  }

  .page-container {
    width: 100%;
  }
}
</style>